import { Component } from '@theme/component';

/**
 * Cart attribute the selected rate is persisted under — read back by
 * snippets/cart-shipping-selected.liquid to render it below the subtotal
 * even after a full page reload or a section re-render.
 */
const CART_ATTRIBUTE_KEY = 'Frete selecionado';

/**
 * Shopify's native shipping-rate lookup for the current cart. Used instead
 * of the "Frete Próprio" app proxy (`/apps/frete-proprio/quote/cart`) since
 * 2026-09-10: that app's rate table was calibrated to match the Yampi
 * checkout specifically, which is temporarily disabled (see
 * layout/theme.liquid) while Yampi is down — checkout is the Shopify native
 * one for now, so the estimate shown here must come from Shopify's own
 * shipping zones to actually match what gets charged.
 */
const NATIVE_SHIPPING_RATES_URL = '/cart/shipping_rates.json';

/**
 * Shopify's shipping zones for Brazil are matched by state, and
 * `/cart/shipping_rates.json` 422s with `{"province":["Select a state"]}`
 * when a `shipping_address[zip]` is given without one — confirmed against
 * this store's live rates. ViaCEP resolves CEP → UF for free, no key, no
 * CORS issues (used broadly by Brazilian storefronts for exactly this).
 */
const VIACEP_URL = 'https://viacep.com.br/ws';

/**
 * @typedef {Object} ShippingRate
 * @property {string} serviceCode
 * @property {string} serviceName
 * @property {string | null} [description]
 * @property {number} priceCents
 * @property {number} minBusinessDays
 * @property {number} maxBusinessDays
 * @property {boolean} freeShipping
 */

/**
 * @typedef {Object} CartShippingEstimatorRefs
 * @property {HTMLDialogElement} modal
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} zip
 * @property {HTMLButtonElement} submit
 * @property {HTMLParagraphElement} error
 * @property {HTMLParagraphElement} loading
 * @property {HTMLParagraphElement} resultsTitle
 * @property {HTMLUListElement} results
 */

/**
 * "Frete" pill in the cart drawer: opens a mini modal with a single CEP
 * field and lists the shipping rates Shopify's native rate engine returns
 * for the current cart — the same ones the Shopify checkout will charge.
 *
 * @extends {Component<CartShippingEstimatorRefs>}
 */
class CartShippingEstimator extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  open() {
    const { modal, zip } = this.refs;
    if (modal.open) return;
    modal.showModal();
    zip.focus();
  }

  close() {
    const { modal } = this.refs;
    this.#activeFetch?.abort();
    if (modal.open) modal.close();
  }

  /**
   * Closes when the click lands on the backdrop rather than the panel.
   *
   * Deliberately does NOT use `event.target === this.refs.modal`: the
   * theme's `@theme/component` event delegation (assets/component.js)
   * resolves a click with no closer `on:click` ancestor by walking up to
   * this dialog's own `on:click`, and — confirmed by inspecting its
   * source — wraps the event in a Proxy whose `target` getter always
   * returns the *matched* element (this dialog), not the real click
   * target. That makes `event.target` equal `this.refs.modal` for every
   * click inside the panel that isn't its own `on:click` owner (the CEP
   * input, the Calcular button, a rendered rate row), closing the modal
   * before a shopper can ever pick a rate. `clientX`/`clientY` pass
   * through that Proxy unmodified, so a geometric hit-test against the
   * panel's own rect is what actually distinguishes "backdrop" from
   * "panel content" here.
   * @param {MouseEvent} event
   */
  onBackdropClick(event) {
    const panel = this.refs.modal.querySelector('.cart-shipping__panel');
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const clickedInsidePanel =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!clickedInsidePanel) this.close();
  }

  /**
   * Escape closes only the mini modal; the cart drawer listens for Escape on
   * the document and would otherwise close together with it.
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  /**
   * Keeps the field as `00000-000` while typing.
   * @param {InputEvent} event
   */
  formatZip(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const digits = input.value.replace(/\D/g, '').slice(0, 8);
    input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  /**
   * @param {SubmitEvent} event
   */
  async calculate(event) {
    event.preventDefault();
    // Belt-and-suspenders: a submit click bubbles up through the dialog
    // afterwards, and onBackdropClick only closes when event.target is the
    // dialog itself (never the button), but stopping propagation here
    // removes any chance of that bubble interacting with it at all.
    event.stopPropagation();

    const { zip, results, resultsTitle, error, loading } = this.refs;
    const digits = zip.value.replace(/\D/g, '');

    results.hidden = true;
    resultsTitle.hidden = true;
    results.replaceChildren();

    if (digits.length !== 8) {
      this.#showError(this.dataset.errorInvalid || 'Informe um CEP válido.');
      zip.focus();
      return;
    }

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    error.hidden = true;
    loading.hidden = false;
    this.setAttribute('aria-busy', 'true');

    try {
      const province = await this.#resolveProvince(digits, abortController.signal);
      if (!province) {
        this.#showError(this.dataset.errorInvalid || 'Informe um CEP válido.');
        zip.focus();
        return;
      }

      const url = new URL(NATIVE_SHIPPING_RATES_URL, window.location.origin);
      url.searchParams.set('shipping_address[zip]', digits);
      url.searchParams.set('shipping_address[country]', 'BR');
      url.searchParams.set('shipping_address[province]', province);

      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: abortController.signal,
      });

      if (!response.ok) {
        this.#showError(
          response.status === 422
            ? this.dataset.errorInvalid || 'Informe um CEP válido.'
            : this.dataset.errorUnavailable || 'Não foi possível calcular o frete para este CEP.',
        );
        return;
      }

      const data = await response.json();
      const rates = (data.shipping_rates ?? []).map((rate) => this.#fromNativeRate(rate));

      if (rates.length === 0) {
        this.#showError(this.dataset.errorEmpty || 'Nenhuma opção de entrega para este CEP.');
        return;
      }

      this.#renderRates(rates);
    } catch (fetchError) {
      if (fetchError instanceof Error && fetchError.name === 'AbortError') return;
      this.#showError(this.dataset.errorUnavailable || 'Não foi possível calcular o frete para este CEP.');
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      loading.hidden = true;
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * Resolves a CEP to its state abbreviation via ViaCEP — required because
   * `/cart/shipping_rates.json` 422s without `shipping_address[province]`
   * for this store's (state-based) shipping zones. Returns `null` for an
   * invalid/unknown CEP or a network failure.
   * @param {string} digits 8-digit CEP
   * @param {AbortSignal} signal
   * @returns {Promise<string | null>}
   */
  async #resolveProvince(digits, signal) {
    try {
      const response = await fetch(`${VIACEP_URL}/${digits}/json/`, { signal });
      if (!response.ok) return null;
      const data = await response.json();
      return data.erro ? null : data.uf || null;
    } catch {
      return null;
    }
  }

  /**
   * Maps a `/cart/shipping_rates.json` entry to this component's internal
   * {@link ShippingRate} shape. Native rates carry the delivery window as
   * either `delivery_days` (a `[min, max]` business-day count) or
   * `delivery_range` (two ISO datetimes) depending on the shipping method —
   * both are handled, business-days taking precedence when both are present.
   * @param {{name: string, presentment_name?: string, code: string, price: string, description?: string | null, delivery_days?: [number, number] | null, delivery_range?: [string, string] | null}} rate
   * @returns {ShippingRate}
   */
  #fromNativeRate(rate) {
    const priceCents = Math.round(parseFloat(rate.price) * 100);
    const [minBusinessDays, maxBusinessDays] = Array.isArray(rate.delivery_days) ? rate.delivery_days : [NaN, NaN];
    return {
      serviceCode: rate.code,
      serviceName: rate.presentment_name || rate.name,
      description: rate.description || this.#formatDeliveryRange(rate.delivery_range),
      priceCents,
      minBusinessDays,
      maxBusinessDays,
      freeShipping: priceCents === 0,
    };
  }

  /**
   * @param {[string, string] | null | undefined} range
   * @returns {string}
   */
  #formatDeliveryRange(range) {
    if (!Array.isArray(range) || range.length !== 2) return '';
    const [min, max] = range.map((iso) => new Date(iso));
    if (Number.isNaN(min.getTime()) || Number.isNaN(max.getTime())) return '';
    const formatter = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });
    const minLabel = formatter.format(min);
    const maxLabel = formatter.format(max);
    return minLabel === maxLabel ? `chega ${minLabel}` : `chega entre ${minLabel} e ${maxLabel}`;
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { error } = this.refs;
    error.textContent = message;
    error.hidden = false;
  }

  /**
   * @param {ShippingRate[]} rates
   */
  #renderRates(rates) {
    const { results, resultsTitle } = this.refs;
    const groupName = `${this.id || 'cart-shipping'}-rate`;
    const items = rates.map((rate) => {
      const item = document.createElement('li');
      item.className = 'cart-shipping__rate';

      const button = document.createElement('label');
      button.className = 'cart-shipping__rate-button';

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = groupName;
      radio.className = 'cart-shipping__rate-radio';
      radio.setAttribute('aria-label', rate.serviceName);
      radio.addEventListener('change', (event) => {
        event.stopPropagation();
        this.#selectRate(rate);
      });

      const content = document.createElement('span');
      content.className = 'cart-shipping__rate-content';

      const label = document.createElement('span');
      label.className = 'cart-shipping__rate-name';
      label.textContent = rate.serviceName;

      const eta = this.#formatEta(rate);
      if (eta) {
        const etaElement = document.createElement('span');
        etaElement.className = 'cart-shipping__rate-eta';
        etaElement.textContent = eta;
        label.append(etaElement);
      }

      const price = document.createElement('span');
      price.className = 'cart-shipping__rate-price';
      price.textContent = this.#formatPrice(rate);

      content.append(label, price);
      button.append(radio, content);
      item.append(button);
      return item;
    });

    results.replaceChildren(...items);
    results.hidden = false;
    resultsTitle.hidden = false;
  }

  /**
   * Persists the chosen rate as a cart attribute (so it survives reloads and
   * drawer/section re-renders — see snippets/cart-shipping-selected.liquid,
   * which reads the same attribute server-side), broadcasts it so the
   * persistent summary below the subtotal updates instantly, then dismisses
   * the mini modal. This is an informational estimate only: it doesn't
   * change `cart.total_price` or bind Shopify to that carrier — the real
   * rate is still decided at checkout.
   *
   * @param {ShippingRate} rate
   */
  async #selectRate(rate) {
    const price = this.#formatPrice(rate);
    const eta = this.#formatEta(rate);
    const label = eta ? `${rate.serviceName} · ${price} · ${eta}` : `${rate.serviceName} · ${price}`;

    this.setAttribute('aria-busy', 'true');
    try {
      await fetch(`${Theme.routes.cart_url}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [CART_ATTRIBUTE_KEY]: label } }),
      });
      document.dispatchEvent(new CustomEvent('cart:shipping-selected', { bubbles: true, detail: { label } }));
      this.close();
    } catch {
      this.#showError(this.dataset.errorUnavailable || 'Não foi possível calcular o frete para este CEP.');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {ShippingRate} rate
   * @returns {string}
   */
  #formatPrice(rate) {
    if (rate.freeShipping || rate.priceCents === 0) return this.dataset.labelFree || 'Grátis';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(rate.priceCents / 100);
  }

  /**
   * @param {ShippingRate} rate
   * @returns {string}
   */
  #formatEta(rate) {
    const { minBusinessDays, maxBusinessDays } = rate;
    if (!Number.isFinite(minBusinessDays) || !Number.isFinite(maxBusinessDays)) return rate.description || '';
    if (minBusinessDays === maxBusinessDays) {
      return maxBusinessDays === 1 ? '1 dia útil' : `até ${maxBusinessDays} dias úteis`;
    }
    return `${minBusinessDays} a ${maxBusinessDays} dias úteis`;
  }
}

if (!customElements.get('cart-shipping-estimator')) {
  customElements.define('cart-shipping-estimator', CartShippingEstimator);
}
